import React, { createContext, useContext, useMemo, useEffect, useRef } from 'react';
import { useGetBrandingConfig } from './useGetBrandingConfig';

interface BrandingAsset {
  type?: string;
  url?: string;
  is_external?: boolean;
}

export interface BrandingConfig {
  logo?: BrandingAsset;
  favicon?: BrandingAsset;
  pageTitlePrefix?: string | null;
}

interface RawBrandingConfig {
  logo?: BrandingAsset;
  'fav-icon'?: BrandingAsset;
  'page-title-prefix'?: string | null;
}

export interface BrandingContextValue {
  config: BrandingConfig | null;
  appLogoUrl: string | null;
  appFaviconUrl: string | null;
  pageTitlePrefix: string | null;
  isLoading: boolean;
  isEnabled: boolean;
  isResolved: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({
  config: null,
  appLogoUrl: null,
  appFaviconUrl: null,
  pageTitlePrefix: null,
  isLoading: false,
  isEnabled: false,
  isResolved: false,
});

function normalizeInternalUrl(base: string, pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const cleanedBase = (base ?? '').replace(/\/$/, '');
  const cleanedPath = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${cleanedBase}${cleanedPath}`;
}

function normalizeConfig(raw: RawBrandingConfig): BrandingConfig {
  return {
    logo: raw.logo,
    favicon: raw['fav-icon'],
    pageTitlePrefix: raw['page-title-prefix'] ?? null,
  };
}

function buildAssetUrl(asset: BrandingAsset | undefined, apiBaseUrl: string): string | null {
  if (!asset?.url) return null;
  if (asset.is_external) return asset.url;
  return normalizeInternalUrl(apiBaseUrl, asset.url);
}

export interface BrandingProviderProps {
  children: React.ReactNode;
  enabled: boolean;
  /** API base URL for resolving internal asset paths */
  apiBaseUrl: string;
  /** Title shown when no branding is configured (e.g. "Wealthlane Customer") */
  fallbackTitle: string;
  /** Fallback favicon path relative to public folder */
  fallbackFavicon?: string;
  /** Loading spinner element shown while branding resolves */
  loadingElement?: React.ReactNode;
}

export const BrandingProvider = ({
  children,
  enabled,
  apiBaseUrl,
  fallbackTitle,
  fallbackFavicon = '/favicon.ico',
  loadingElement,
}: BrandingProviderProps): JSX.Element => {
  const { data, isLoading } = useGetBrandingConfig({ enabled });

  const titleSetRef = useRef<boolean>(false);
  const currentFaviconUrlRef = useRef<string | null>(null);

  const parsed = useMemo((): BrandingConfig | null => {
    if (!enabled) return null;
    if (data == null || data === '') return null;
    try {
      const raw = JSON.parse(data) as RawBrandingConfig;
      return normalizeConfig(raw);
    } catch {
      return null;
    }
  }, [data, enabled]);

  const appLogoUrl = useMemo(() => buildAssetUrl(parsed?.logo, apiBaseUrl), [parsed, apiBaseUrl]);
  const appFaviconUrl = useMemo(() => buildAssetUrl(parsed?.favicon, apiBaseUrl), [parsed, apiBaseUrl]);
  const pageTitlePrefix = useMemo(() => parsed?.pageTitlePrefix ?? null, [parsed]);

  const configResolved = !isLoading;

  useEffect(() => {
    if (enabled && !configResolved) return;

    const baseFaviconUrl = appFaviconUrl ?? fallbackFavicon;
    if (currentFaviconUrlRef.current === baseFaviconUrl) return;
    currentFaviconUrlRef.current = baseFaviconUrl;

    const faviconUrl = `${baseFaviconUrl}${baseFaviconUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;

    const existingIcons = document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]');
    existingIcons.forEach((icon) => icon.remove());

    const createFaviconLink = (rel: string) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = faviconUrl;
      document.head.appendChild(link);
    };

    createFaviconLink('icon');
    createFaviconLink('shortcut icon');
  }, [enabled, configResolved, appFaviconUrl, fallbackFavicon]);

  useEffect(() => {
    if (enabled && !configResolved) return;

    if (pageTitlePrefix) {
      const appType = fallbackTitle.replace(/^Wealthlane\s*/i, '');
      document.title = `${pageTitlePrefix} ${appType}`.trim();
    } else {
      document.title = fallbackTitle;
    }
    titleSetRef.current = true;
  }, [enabled, configResolved, pageTitlePrefix, fallbackTitle]);

  if (enabled && isLoading) {
    return <>{loadingElement ?? null}</>;
  }

  return (
    <BrandingContext.Provider
      value={{
        config: parsed,
        appLogoUrl,
        appFaviconUrl,
        pageTitlePrefix,
        isLoading: enabled ? isLoading : false,
        isEnabled: enabled,
        isResolved: !enabled || configResolved,
      }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = (): BrandingContextValue => useContext(BrandingContext);
